import { v4 as uuidv4 } from 'uuid';
import { indexedDbService } from './indexedDbService';
import { ChangeType, PendingChange } from './indexedDbService';
import { apiClient } from './apiClient';
import { offlineService } from './offlineService';
import { apiHealthService } from './apiHealthService';

// Create a simple network service if it doesn't exist
export const networkService = {
  isOnline: async (): Promise<boolean> => {
    return navigator.onLine;
  }
};

/**
 * Interface for appointment exceptions
 */
export interface AppointmentException {
  id: string;
  appointmentId: string;
  exceptionDate: string;
  isDeleted: boolean;
  newStartTime?: string;
  newEndTime?: string;
  newTitle?: string;
  newDescription?: string;
  newAddress?: string;
  newIsAllDay?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Service for managing appointment exceptions
 */
export class AppointmentExceptionService {
  private static instance: AppointmentExceptionService;
  private readonly apiUrl: string;

  private constructor() {
    this.apiUrl = `${process.env.REACT_APP_API_URL || `${apiHealthService.getApiUrl()}/api`}/appointmentexceptions`;
  }

  public static getInstance(): AppointmentExceptionService {
    if (!AppointmentExceptionService.instance) {
      AppointmentExceptionService.instance = new AppointmentExceptionService();
    }
    return AppointmentExceptionService.instance;
  }

  /**
   * Get all exceptions for a specific appointment
   */
  async getExceptionsForAppointment(appointmentId: string): Promise<AppointmentException[]> {
    // Check if we're offline
    const isOffline = !offlineService.getOnlineStatus();
    
    if (isOffline) {
      console.log(`Offline mode: Getting exceptions for appointment ${appointmentId} from IndexedDB`);
      try {
        return await indexedDbService.getAppointmentExceptions(appointmentId);
      } catch (error) {
        console.error(`Error getting exceptions from IndexedDB for appointment ${appointmentId}:`, error);
        return [];
      }
    }
    
    try {
      // Use apiClient instead of fetch
      const response = await apiClient.get(`/appointmentexceptions/appointment/${appointmentId}`);
      const exceptions = response.data;
      
      // Save to IndexedDB for offline use
      await indexedDbService.saveAppointmentExceptions(exceptions);
      
      return exceptions;
    } catch (error) {
      console.error(`Error fetching exceptions for appointment ${appointmentId}:`, error);
      
      // Try to get from IndexedDB as fallback
      try {
        return await indexedDbService.getAppointmentExceptions(appointmentId);
      } catch (dbError) {
        console.error(`IndexedDB fallback failed for appointment ${appointmentId}:`, dbError);
        return [];
      }
    }
  }

  /**
   * Create a new appointment exception
   */
  async createException(exception: Omit<AppointmentException, 'id' | 'createdAt' | 'updatedAt'>): Promise<AppointmentException> {
    // Check if we're offline
    const isOffline = !offlineService.getOnlineStatus();
    
    if (isOffline) {
      console.log('Offline mode: Creating exception in IndexedDB');
      try {
        // Generate a temporary ID
        const newException: AppointmentException = {
          ...exception,
          id: uuidv4(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Save to IndexedDB
        await indexedDbService.saveAppointmentException(newException);
        
        // Add a pending change
        await indexedDbService.addPendingChange({
          entityId: newException.id,
          entityType: 'appointmentException',
          changeType: ChangeType.CREATE,
          data: newException,
          endpoint: '/api/appointmentexceptions',
          method: 'POST'
        });
        
        return newException;
      } catch (error) {
        console.error('Error creating exception in offline mode:', error);
        throw error;
      }
    }
    
    try {
      // Use apiClient instead of fetch
      const response = await apiClient.post('/appointmentexceptions', exception);
      const newException = response.data;
      
      // Save to IndexedDB for offline use
      await indexedDbService.saveAppointmentException(newException);
      
      return newException;
    } catch (error) {
      console.error('Error creating exception:', error);
      throw error;
    }
  }

  /**
   * Update an existing appointment exception
   */
  async updateException(id: string, exception: Partial<AppointmentException>): Promise<AppointmentException> {
    // Check if we're offline
    const isOffline = !offlineService.getOnlineStatus();
    
    if (isOffline) {
      console.log(`Offline mode: Updating exception ${id} in IndexedDB`);
      try {
        // Get the existing exception
        const existingException = await indexedDbService.getAppointmentException(id);
        if (!existingException) {
          throw new Error(`Exception ${id} not found in offline storage`);
        }
        
        // Update the exception
        const updatedException: AppointmentException = {
          ...existingException,
          ...exception,
          updatedAt: new Date().toISOString()
        };
        
        // Save to IndexedDB
        await indexedDbService.saveAppointmentException(updatedException);
        
        // Add a pending change
        await indexedDbService.addPendingChange({
          entityId: id,
          entityType: 'appointmentException',
          changeType: ChangeType.UPDATE,
          data: updatedException,
          endpoint: `/api/appointmentexceptions/${id}`,
          method: 'PUT'
        });
        
        return updatedException;
      } catch (error) {
        console.error(`Error updating exception ${id} in offline mode:`, error);
        throw error;
      }
    }
    
    try {
      // Use apiClient instead of fetch
      const response = await apiClient.put(`/appointmentexceptions/${id}`, exception);
      const updatedException = response.data;
      
      // Save to IndexedDB for offline use
      await indexedDbService.saveAppointmentException(updatedException);
      
      return updatedException;
    } catch (error) {
      console.error(`Error updating exception ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete an appointment exception
   */
  async deleteException(id: string): Promise<void> {
    // Check if we're offline
    const isOffline = !offlineService.getOnlineStatus();
    
    if (isOffline) {
      console.log(`Offline mode: Marking exception ${id} as deleted in IndexedDB`);
      try {
        // Get the existing exception
        const existingException = await indexedDbService.getAppointmentException(id);
        if (!existingException) {
          throw new Error(`Exception ${id} not found in offline storage`);
        }
        
        // Mark as deleted but keep in IndexedDB for sync
        const deletedException: AppointmentException = {
          ...existingException,
          isDeleted: true,
          updatedAt: new Date().toISOString()
        };
        
        // Save to IndexedDB
        await indexedDbService.saveAppointmentException(deletedException);
        
        // Add a pending change
        await indexedDbService.addPendingChange({
          entityId: id,
          entityType: 'appointmentException',
          changeType: ChangeType.DELETE,
          data: null,
          endpoint: `/api/appointmentexceptions/${id}`,
          method: 'DELETE'
        });
      } catch (error) {
        console.error(`Error deleting exception ${id} in offline mode:`, error);
        throw error;
      }
    } else {
      try {
        // Use apiClient instead of fetch
        await apiClient.delete(`/appointmentexceptions/${id}`);
        
        // Remove from IndexedDB
        await indexedDbService.deleteAppointmentException(id);
      } catch (error) {
        console.error(`Error deleting exception ${id}:`, error);
        throw error;
      }
    }
  }
}

export const appointmentExceptionService = AppointmentExceptionService.getInstance(); 